/**
 * ComplianceMetrics — Closed-loop session compliance display.
 * Shows zone, duration, distance, and intensity accuracy scores.
 */

import { cn } from '@/lib/utils';
import { VerdictBadge } from '@/components/shared/VerdictBadge';
import type { SessionComplianceData } from '@/lib/analyticsApi';

interface ComplianceMetricsProps {
  compliance: SessionComplianceData;
  className?: string;
}

interface MetricBarProps {
  label: string;
  value: number | null;
  delta?: number | null;
  unit?: string;
}

function MetricBar({ label, value, delta, unit = '%' }: MetricBarProps) {
  if (value === null || value === undefined) return null;

  const pct = Math.min(value * 100, 100);
  const color =
    pct >= 90 ? 'bg-green-400' :
    pct >= 70 ? 'bg-amber-400' :
    'bg-red-400';

  const textColor =
    pct >= 90 ? 'text-green-400' :
    pct >= 70 ? 'text-amber-400' :
    'text-red-400';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className={cn('text-[11px] font-mono tabular-nums', textColor)}>
          {pct.toFixed(0)}{unit}
          {delta != null && (
            <span className="text-muted-foreground/60 ml-1">
              ({delta >= 0 ? '+' : ''}{delta.toFixed(0)}%)
            </span>
          )}
        </span>
      </div>
      <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', color)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function ComplianceMetrics({ compliance, className }: ComplianceMetricsProps) {
  const qualityPct = Math.round(compliance.quality_score * 100);
  const qualityColor =
    qualityPct >= 90 ? 'text-green-400' :
    qualityPct >= 70 ? 'text-amber-400' :
    'text-red-400';

  return (
    <div className={cn('rounded-lg border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm p-4 space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />
          <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">
            Compliance Analysis
          </span>
        </div>
        {compliance.verdict && (
          <VerdictBadge
            verdict={compliance.verdict}
            confidence={compliance.verdict_confidence}
            size="md"
          />
        )}
      </div>

      {/* Quality Score - Hero */}
      <div className="text-center py-2">
        <div className={cn('text-3xl font-mono font-light tabular-nums', qualityColor)}>
          {qualityPct}
          <span className="text-lg text-muted-foreground/50">%</span>
        </div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground mt-1">
          Quality Score
        </div>
      </div>

      {/* Metric Bars */}
      <div className="space-y-3">
        <MetricBar
          label="Zone Match"
          value={compliance.zone_match_score}
        />
        <MetricBar
          label="Duration"
          value={compliance.duration_accuracy}
          delta={compliance.duration_delta_pct}
        />
        <MetricBar
          label="Distance"
          value={compliance.distance_accuracy}
          delta={compliance.distance_delta_pct}
        />
        <MetricBar
          label="Intensity"
          value={compliance.intensity_accuracy}
        />
      </div>

      {/* Flags */}
      {compliance.flags.length > 0 && (
        <div className="pt-2 border-t border-white/[0.06]">
          <div className="flex flex-wrap gap-1.5">
            {compliance.flags.map((flag, i) => (
              <span
                key={i}
                className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-400 border border-amber-400/20"
              >
                {flag.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Confidence */}
      <div className="text-[9px] font-mono text-muted-foreground/40 text-right">
        match: {compliance.match_confidence}
        {compliance.intensity_direction && ` | intensity: ${compliance.intensity_direction}`}
      </div>
    </div>
  );
}
